/**
 * The time zones the picker offers (knowledge doc section 20).
 *
 * Its own module rather than a helper beside the component, so the component file
 * exports a component and nothing else — which is what fast refresh needs to swap
 * it without reloading the page.
 */

const FALLBACK_ZONES = [
  'UTC',
  'Europe/London',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Australia/Sydney',
];

function platformZones(): string[] {
  const supported: unknown = (Intl as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf;
  if (typeof supported !== 'function') return FALLBACK_ZONES;
  try {
    return (supported as (key: string) => string[])('timeZone');
  } catch {
    return FALLBACK_ZONES;
  }
}

/**
 * The zones to offer.
 *
 * Two corrections to the platform's list, both found by testing rather than
 * guessed at. `Intl.supportedValuesOf('timeZone')` omits **UTC** on current ICU,
 * which is the one zone a fleet console must always offer — it is the instance
 * default for most installations and the zone everything is stored in. And the list
 * uses whichever spelling ICU canonicalizes to, so a stored `Asia/Kolkata` can be
 * absent while `Asia/Calcutta` is present; including `current` keeps a person's own
 * setting selectable instead of leaving the control blank.
 */
export function supportedTimezones(current?: string | null): string[] {
  const others = new Set(platformZones());
  others.delete('UTC');
  if (current !== undefined && current !== null && current !== 'UTC') others.add(current);
  return ['UTC', ...[...others].sort((a, b) => a.localeCompare(b))];
}
